/**
 * Semantic color tokens.
 *
 * Verbatim of what used to live in frontend/src/theme/colorTokens.ts +
 * console/src/theme/colorTokens.ts (which were byte-identical copies).
 * Single source of truth now — consumers re-export from here via the
 * synced design-tokens/ mirror.
 *
 * For brand palette (primary/secondary), see ./brand.ts.
 */

// ─── Status Colors ──────────────────────────────────────────────────
// Used by StatusDot, chips, table badges, KPI cards, etc.
export const STATUS_COLORS: Record<string, string> = {
  // greens — success states
  success: '#2E7D32',
  active: '#2E7D32',
  approved: '#2E7D32',
  confirmed: '#2E7D32',
  paid: '#2E7D32',
  ok: '#2E7D32',
  create: '#2E7D32',
  accepted: '#2E7D32',
  hired: '#2E7D32',

  // blues — informational states
  info: '#1565C0',
  probation: '#1565C0',
  onboarding: '#1565C0',
  calculated: '#1565C0',
  partial: '#1565C0',
  update: '#1565C0',
  sent: '#1565C0',
  pre_boarding: '#26A69A',

  // ambers / orange — warning states
  warning: '#E65100',
  pending: '#E65100',
  notice_period: '#E65100',
  suspended: '#E65100',
  expired: '#E65100',
  prospective: '#E65100',
  reject: '#E65100',

  // reds — error / destructive states
  error: '#C62828',
  inactive: '#C62828',
  terminated: '#C62828',
  exited: '#C62828',
  rejected: '#C62828',
  voided: '#C62828',
  denied: '#C62828',
  delete: '#C62828',
  void: '#C62828',
  declined: '#C62828',
  withdrawn: '#BDBDBD',

  // neutrals
  draft: '#78909C',
  cancelled: '#78909C',
  default: '#78909C',
  former: '#78909C',
};

export function getStatusColor(status: string): string {
  const key = status.toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_COLORS[key] || STATUS_COLORS.default;
}

// ─── Module Colors ──────────────────────────────────────────────────
export const MODULE_COLORS: Record<string, string> = {
  accounting: '#1565C0',
  hr: '#7B1FA2',
  payroll: '#00695C',
  tenants: '#E65100',
  budget: '#2E7D32',
  group: '#37474F',
  settings: '#455A64',
  dashboard: '#0277BD',
  ai: '#6A1B9A',
  comms: '#0288D1',
  auth: '#37474F',
  leave: '#0277BD',
  recruitment: '#AD1457',
  performance: '#4527A0',
};

// ─── Notification Type Colors ───────────────────────────────────────
export const NOTIFICATION_COLORS: Record<string, string> = {
  payroll_processed: '#2E7D32',
  leave_approved: '#1565C0',
  leave_rejected: '#C62828',
  rent_reminder: '#E65100',
  intercompany_confirmation: '#6A1B9A',
  message: '#0288D1',
  welcome: '#FF6F00',
  setup_reminder: '#1a3c8f',
  holiday_reminder: '#00897B',
  industry_tip: '#5C6BC0',
  default: '#78909C',
};

export function getNotificationColor(type: string | null): string {
  return NOTIFICATION_COLORS[type || ''] || NOTIFICATION_COLORS.default;
}

// ─── Password Strength Colors ───────────────────────────────────────
export const PASSWORD_STRENGTH_COLORS = {
  weak: '#E53935',
  fair: '#FB8C00',
  good: '#FDD835',
  strong: '#43A047',
  inactive: '#E0E0E0',
};

// ─── Recruitment Pipeline Stage Colors ──────────────────────────────
export const PIPELINE_STAGE_COLORS: Record<string, string> = {
  applied: '#90CAF9',
  screening: '#CE93D8',
  interview: '#FFB74D',
  offer: '#81C784',
  accepted: '#4CAF50',
  pre_boarding: '#26A69A',
  hired: '#2E7D32',
  rejected: '#EF5350',
  withdrawn: '#BDBDBD',
};

// ─── Onboarding Phase Colors ────────────────────────────────────────
export const ONBOARDING_PHASE_COLORS = [
  '#6366F1', // indigo
  '#0EA5E9', // sky
  '#F59E0B', // amber
  '#10B981', // emerald
  '#8B5CF6', // violet
  '#EF4444', // red
];

// ─── Social Media Brand Colors ──────────────────────────────────────
// Third-party brand guidelines — do NOT change.
export const SOCIAL_BRAND_COLORS: Record<string, string> = {
  linkedin: '#0A66C2',
  twitter: '#000000',
  facebook: '#1877F2',
  instagram: '#E4405F',
  youtube: '#FF0000',
  whatsapp: '#25D366',
};

// ─── Channel Colors ─────────────────────────────────────────────────
export const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: '#25D366',
  email: '#1565C0',
  sms: '#FF9800',
  in_app: '#6A1B9A',
};

// ─── PDF / Print Export Colors ──────────────────────────────────────
export const EXPORT_COLORS = {
  headerBg: [26, 60, 143] as [number, number, number],
  headerText: 255,
  altRowBg: [245, 246, 250] as [number, number, number],
  printText: '#1A1A2E',
  printHeading: '#1565C0',
  printHeaderBg: '#1565C0',
  printHeaderText: '#ffffff',
  printBorder: '#e0e0e0',
  printAltRow: '#f5f6fa',
};

/**
 * Get palette-aware export colors for PDF/print.
 * Call inside a React component with useTheme() result.
 */
export function getExportColors(primaryHex: string, bgHex: string) {
  const r = parseInt(primaryHex.slice(1, 3), 16);
  const g = parseInt(primaryHex.slice(3, 5), 16);
  const b = parseInt(primaryHex.slice(5, 7), 16);
  const br = parseInt(bgHex.slice(1, 3), 16);
  const bg = parseInt(bgHex.slice(3, 5), 16);
  const bb = parseInt(bgHex.slice(5, 7), 16);
  return {
    headerBg: [r, g, b] as [number, number, number],
    headerText: 255,
    altRowBg: [br, bg, bb] as [number, number, number],
    printText: '#1A1A2E',
    printHeading: primaryHex,
    printHeaderBg: primaryHex,
    printHeaderText: '#ffffff',
    printBorder: '#e0e0e0',
    printAltRow: bgHex,
  };
}

// ─── Workflow Recipe Colors ─────────────────────────────────────────
export const RECIPE_COLORS: Record<string, string> = {
  green: '#4CAF50',
  orange: '#FF9800',
  blue: '#2196F3',
  cyan: '#00BCD4',
  purple: '#9C27B0',
  brown: '#795548',
  red: '#F44336',
  teal: '#009688',
  indigo: '#3F51B5',
  pink: '#E91E63',
  lime: '#CDDC39',
  amber: '#FFC107',
};

// ─── Status Soft Backgrounds ────────────────────────────────────────
export const STATUS_BG_COLORS = {
  error: '#FFEBEE',
  errorDeep: '#FEE2E2',
  warning: '#FFF3E0',
  warningDeep: '#FFF7ED',
  info: '#E3F2FD',
  success: '#E8F5E9',
} as const;

export const STATUS_FG_COLORS = {
  error: '#C62828',
  warning: '#E65100',
  warningDeep: '#C2410C',
  warningDeeper: '#9A3412',
  info: '#1565C0',
  success: '#2E7D32',
  neutral: '#6B7280',
} as const;

// ─── Announcement Category Colors ───────────────────────────────────
export const ANNOUNCEMENT_CATEGORY_COLORS: Record<string, string> = {
  general: '#6B7280',
  policy: '#7C3AED',
  event: '#2563EB',
  hr: '#059669',
  safety: '#DC2626',
  it: '#D97706',
};

// ─── TA Sync Section Colors ─────────────────────────────────────────
export const TA_SYNC_SECTION_COLORS: Record<string, string> = {
  invoice: '#1a3c8f',
  profile: '#0288D1',
  academic: '#2E7D32',
  guardian: '#E65100',
  other: '#757575',
};

export const TA_SYNC_META_COLOR = '#7B1FA2';

// ─── Impersonation Banner Colors ────────────────────────────────────
export const IMPERSONATION_COLORS = {
  bg: '#7f1d1d',
  fg: '#FFFFFF',
  accent: '#fecaca',
  buttonHover: '#F3F4F6',
} as const;

// ─── Cookie Consent Overlay ─────────────────────────────────────────
export const COOKIE_CONSENT_COLORS = {
  bg: '#1A1A2E',
  fg: '#FFFFFF',
  mutedFg: '#D1D5DB',
  linkFg: '#93C5FD',
  outlineBorder: '#4B5563',
  acceptHover: '#1E3F9E',
} as const;

// ─── Platform Admin Shell ───────────────────────────────────────────
export const PLATFORM_SHELL_COLORS = {
  sidebarBg: '#0F1117',
  sidebarFg: '#E4E7EB',
  sidebarLabel: '#6B7280',
  sidebarLinkFg: '#9CA3AF',
  sidebarLinkActiveFg: '#FFFFFF',
  mainBg: '#F7F8FA',
  // Active nav row tint — translucent blue over dark sidebar bg.
  sidebarLinkActiveBg: 'rgba(59, 130, 246, 0.12)',
  // Hover tint for nav rows + section headers over dark sidebar bg.
  sidebarHoverBg: 'rgba(255, 255, 255, 0.04)',
  // Translucent borders/dividers over dark sidebar bg (footer, mobile AppBar).
  sidebarBorder: 'rgba(255, 255, 255, 0.08)',
} as const;

// ─── Rich Text Editor ───────────────────────────────────────────────
export const HIGHLIGHT_MARK_BG = '#FEF08A';

// ─── Neutral Scale (Tailwind gray, used by Tremor-density console) ──
// Borders, table head bg, muted text. Sampled from console-tremor-density-mockup.html.
export const NEUTRAL_SCALE = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
} as const;

// ─── Neutral Primitives ─────────────────────────────────────────────
export const NEUTRAL_COLORS = {
  white: '#FFFFFF',
  paperDark: '#1A1C25',
  textInverted: '#E4E6EB',
  divider: '#E5E7EB',
  dividerLight: '#F3F4F6',
  textDark: '#1A1A2E',
  muted: '#9E9E9E',
  surfaceMuted: '#F7F8FA',
} as const;

// ─── Navbar Chrome ──────────────────────────────────────────────────
export const NAVBAR_COLORS = {
  bgDark: '#0F1117',
  borderDark: '#2D303A',
  borderLight: '#E8EAF0',
  hamburgerBg: '#8A9097',
  hamburgerBorder: '#7A7A7A',
} as const;
